import { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { Tr } from '@utils';
import { CarListBand } from '@services';
import { Divider, Grid } from '@mui/material';
import { VoltaageModalDialog, IVoltaageModalDialogProps, FormikTextfield, FormikNumber, FormikSubmitButton } from '@components/common';

type FormFields = Extendable<{
	label: string;
	minBudget: number;
	maxBudget: number;
}>;

export interface ICarListBandAddUpdateDialogProps extends IVoltaageModalDialogProps {
	entity?: CarListBand;
	onPrimaryButtonClick?: (entity: Partial<CarListBand>) => void;
}

export const CarListBandAddUpdateDialog: FC<ICarListBandAddUpdateDialogProps> = ({ entity, onPrimaryButtonClick, ...props }) => {
	const { t } = useTranslation('portal');
	const { open } = props;
	const formik = useFormik<FormFields>({
		initialValues: { label: entity?.label || '', minBudget: entity?.minBudget || '', maxBudget: entity?.maxBudget || '' },
		validateOnChange: true,
		validationSchema: yup.object({
			label: yup.string().required(t('car-list-bands.car-list-band-name-required')),
			minBudget: yup.number().required(t('car-list-bands.min-budget-required')),
			maxBudget: yup.number().required(t('car-list-bands.max-budget-required'))
		}),
		onSubmit: ({ label, minBudget, maxBudget }, api) => {
			api.setSubmitting(false);
			onPrimaryButtonClick?.(new CarListBand({ ...entity, label, minBudget: +minBudget, maxBudget: +maxBudget }));
		}
	});

	useEffect(() => {
		open &&
			formik.resetForm({
				errors: {},
				values: {
					label: entity?.label || '',
					minBudget: entity?.minBudget || '',
					maxBudget: entity?.maxBudget || ''
				}
			});
	}, [open]);

	return (
		<VoltaageModalDialog
			title={
				<Tr.Portal
					path={entity ? 'car-list-bands.update-car-list-band-modal-title' : 'car-list-bands.add-car-list-band-modal-title'}
				/>
			}
			dividers
			{...props}
		>
			<form onSubmit={formik.handleSubmit}>
				<Grid container rowSpacing={4} columnSpacing={3} sx={{ paddingX: 14, paddingY: 8, paddingBottom: 4 }}>
					<Grid item xs={6}>
						<FormikTextfield
							formikApi={formik}
							name="label"
							title={t('car-list-bands.car-list-band-name')}
							placeholder={t('car-list-bands.placeholder.car-list-band-name')}
							fullWidth
							required
						/>
					</Grid>
					<Grid item xs={6}>
						<FormikNumber
							formikApi={formik}
							name="minBudget"
							title={t('car-list-bands.min-budget')}
							placeholder={t('car-list-bands.placeholder.min-budget')}
							fullWidth
							required
							decimal={0}
						/>
					</Grid>
					<Grid item xs={6}>
						<FormikNumber
							formikApi={formik}
							name="maxBudget"
							title={t('car-list-bands.max-budget')}
							placeholder={t('car-list-bands.placeholder.max-budget')}
							fullWidth
							required
							decimal={0}
						/>
					</Grid>
					<Grid item xs={12}>
						<Divider light />
					</Grid>
					<Grid item xs={12} container>
						<FormikSubmitButton formik={formik} data-testid="add-update-car-list-band-form-submit-button">
							<Tr.Portal path={entity ? 'car-list-bands.update-car-list-band' : 'car-list-bands.add-car-list-band'} />
						</FormikSubmitButton>
					</Grid>
				</Grid>
			</form>
		</VoltaageModalDialog>
	);
};
